/**
 * Formats a duration in days into a human-friendly string.
 * Used everywhere the app needs to display plan duration so changing
 * Admin → Plans → "Duration (Days)" cascades immediately across the UI.
 *
 *   formatDuration(2192) => "6 years"
 *   formatDuration(365)  => "1 year"
 *   formatDuration(30)   => "30 days"
 *   formatDuration(0)    => "Lifetime"
 *   formatDuration(36500, { lifetimeAfterYears: 50 }) => "Lifetime"
 */
export function formatDuration(days, opts = {}) {
  const { lifetimeAfterYears = 50 } = opts;
  const d = Number(days);
  if (!d || d <= 0) return 'Lifetime';
  if (d >= lifetimeAfterYears * 365) return 'Lifetime';

  // Years
  if (d >= 360 && d % 365 <= 5) {
    const yrs = Math.round(d / 365);
    return yrs === 1 ? '1 year' : `${yrs} years`;
  }
  // Multi-year approximation (e.g. 2192 = 6yr + 2d → "6 years")
  if (d >= 365 * 2 - 10) {
    const yrs = Math.round(d / 365.25);
    return `${yrs} years`;
  }
  // Months
  if (d >= 28 && d % 30 <= 2) {
    const months = Math.round(d / 30);
    return months === 1 ? '1 month' : `${months} months`;
  }
  if (d >= 60) {
    const months = Math.round(d / 30);
    return `${months} months`;
  }
  // Weeks
  if (d >= 14 && d % 7 === 0) {
    const wks = d / 7;
    return wks === 1 ? '1 week' : `${wks} weeks`;
  }
  return d === 1 ? '1 day' : `${d} days`;
}

/** Compact ("6y", "1y", "30d") variant for tight spaces. */
export function formatDurationShort(days) {
  const d = Number(days);
  if (!d || d <= 0) return '∞';
  if (d >= 365) {
    const yrs = Math.round(d / 365.25);
    return `${yrs}y`;
  }
  if (d >= 30) return `${Math.round(d / 30)}mo`;
  return `${d}d`;
}
